// Main Layout Component for Iron Forums

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/useAuth';
import {
  Home,
  FileText,
  MessageSquare,
  Users,
  BarChart3,
  LogOut,
  Menu,
  X,
  BookOpen,
  BookUser,
  CalendarClock,
  ClipboardList,
  Heart,
  Target,
  Settings,
  ChevronDown,
  Shield,
  ArrowLeft,
  Receipt,
  Megaphone,
  Mail,
  Gavel,
  HelpCircle,
  Inbox,
  NotebookPen,
} from 'lucide-react';
import { isNativePlatform, platform } from '../native';
import { notificationsAPI } from '../api/client';
import { useVisibilityPolling } from '../hooks/useVisibilityPolling';
import { hasGuestPrivileges, isAdminRole, isGodModeRole, isLeadershipRole, roleLabel } from '../utils/roles';
import LapsedMembershipBanner from './LapsedMembershipBanner';
import BrandLogo from './BrandLogo';
import AskIfMark from './AskIfMark';
import AskIfFab from './AskIfFab';
import SupportFeedbackModal from './SupportFeedbackModal';
import MembersOnlyModal from './MembersOnlyModal';
import { BADGES_STALE_EVENT } from '../lib/badges';
import { setAppBadge } from '../native/appBadge';
import {
  askIfReturnPath,
  clearAskIfReturnConversation,
  peekAskIfReturnConversation,
} from '../utils/askIfReturn';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<'connect' | 'sharpen' | 'grow' | 'admin' | null>(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [membersOnlyOpen, setMembersOnlyOpen] = useState(false);
  const [badges, setBadges] = useState<{
    messages: number; meetings: number; accountability: number; support?: number;
  }>({ messages: 0, meetings: 0, accountability: 0, support: 0 });
  const isGuest = hasGuestPrivileges(user);
  const guestLockedPaths = new Set([
    '/commitments',
    '/prayers',
    '/notes',
    '/messages',
    '/meetings',
    '/members',
    '/accountability-groups',
  ]);

  const handleLockedNav = (event: React.MouseEvent) => {
    event.preventDefault();
    setOpenMenu(null);
    setMobileMenuOpen(false);
    setMembersOnlyOpen(true);
  };

  // Unread/pending counts light up nav badge dots (Messages, 1:1 Meetings,
  // Accountability Groups). One aggregate endpoint, one slow poll — cheap
  // enough to run from every page.
  const { refresh: refreshBadges } = useVisibilityPolling({
    fetcher: notificationsAPI.getBadges,
    onSuccess: (counts) => {
      setBadges(counts);
      void setAppBadge(counts.total ?? counts.messages + counts.meetings + counts.accountability + (counts.support ?? 0));
    },
    intervalMs: 30000,
    enabled: !!user,
    onError: (error) => console.error('Error loading badge counts:', error),
  });

  // Pages fire this after they change read state (e.g. opening a message
  // thread) so the counts drop right away instead of on the next slow poll.
  useEffect(() => {
    const onStale = () => void refreshBadges(true);
    window.addEventListener(BADGES_STALE_EVENT, onStale);
    return () => window.removeEventListener(BADGES_STALE_EVENT, onStale);
  }, [refreshBadges]);

  // Close nav dropdowns on an outside tap/click. This must NOT be an onBlur
  // timer on the trigger button: on touch devices (iPad) the blur fires on
  // touchstart and the menu unmounted before the tap's click reached the menu
  // link, so dropdown items did nothing.
  useEffect(() => {
    if (!openMenu) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (target?.closest('[data-nav-dropdown]')) return;
      setOpenMenu(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [openMenu]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileMenuOpen]);

  const fullBleedMobile =
    location.pathname === '/messages' ||
    location.pathname === '/jarvis' ||
    location.pathname.startsWith('/jarvis/');

  // The below-lg phone shell (scroll-locked window, inner-scrolling <main>)
  // is meant for touch devices. Desktop browsers in a sub-1024px window — a
  // Mac split screen or an installed web-app window — must scroll like a
  // normal page instead: desktop Safari shifts the locked viewport to reveal
  // focused fields, sliding the header out of view and covering the bottom of
  // the window with the navy overscroll band (launch backlog L6). Chat-style
  // pages keep the shell everywhere; their composers depend on it.
  useEffect(() => {
    if (isNativePlatform || fullBleedMobile) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    document.documentElement.classList.add('desktop-page-scroll');
    return () => document.documentElement.classList.remove('desktop-page-scroll');
  }, [fullBleedMobile]);

  const connectBadge = badges.meetings;
  const sharpenBadge = badges.accountability;
  const supportBadge = badges.support ?? 0;
  const anyBadge = badges.messages + connectBadge + sharpenBadge + supportBadge > 0;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  type NavLink = {
    path: string;
    label: string;
    icon: React.ComponentType<{ size?: number }>;
    badge?: number;
  };

  // Always-visible: Home, Snapshots, Messages. The brand pillars are
  // dropdowns so Connect / Sharpen / Grow stay one click away.
  const primaryItems: NavLink[] = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/snapshots', label: 'Snapshots', icon: FileText },
    { path: '/messages', label: 'Messages', icon: MessageSquare, badge: badges.messages || undefined },
  ];

  const connectItems: NavLink[] = [
    { path: '/forum', label: 'Forum Home', icon: Users },
    { path: '/members', label: 'Member Directory', icon: BookUser },
    { path: '/meetings', label: '1:1 Meetings', icon: CalendarClock, badge: badges.meetings || undefined },
  ];

  const sharpenItems: NavLink[] = [
    { path: '/snapshots', label: 'Snapshots', icon: FileText },
    { path: '/snapshots?tab=group', label: 'Snapshot Group', icon: FileText },
    {
      path: '/accountability-groups',
      label: 'Accountability Groups',
      icon: Target,
      badge: badges.accountability || undefined,
    },
    { path: '/library', label: 'Library', icon: BookOpen },
  ];

  const growItems: NavLink[] = [
    { path: '/commitments', label: 'Grow Goals', icon: Target },
    { path: '/prayers', label: 'Prayers', icon: Heart },
    { path: '/notes', label: 'Notes', icon: NotebookPen },
  ];

  const navSections: {
    id: 'connect' | 'sharpen' | 'grow';
    label: string;
    icon: React.ComponentType<{ size?: number }>;
    items: NavLink[];
    badge?: number;
  }[] = [
    { id: 'connect', label: 'Connect', icon: Users, items: connectItems, badge: connectBadge || undefined },
    { id: 'sharpen', label: 'Sharpen', icon: BookOpen, items: sharpenItems, badge: sharpenBadge || undefined },
    { id: 'grow', label: 'Grow', icon: Target, items: growItems },
  ];

  const isItemActive = (path: string): boolean => {
    const [pathname, search] = path.split('?');
    if (search) {
      return location.pathname === pathname && location.search === `?${search}`;
    }
    if (pathname === '/') return location.pathname === '/';
    if (pathname === '/snapshots') {
      return location.pathname.startsWith('/snapshots') && location.search !== '?tab=group';
    }
    return location.pathname === pathname || location.pathname.startsWith(`${pathname}/`);
  };

  const isSectionActive = (items: NavLink[]) => items.some((item) => isItemActive(item.path));

  // Leadership dropdown: shared facilitator/admin items first, then an
  // admin-only section (rendered after a divider). The "Members" list is the
  // same page for both — the server scopes facilitators to their own forums.
  type MenuItem = { path: string; label: string; icon: React.ComponentType<{ size?: number }> };
  const leadershipItems: MenuItem[] = [];
  const adminOnlyItems: MenuItem[] = [];
  if (isLeadershipRole(user?.role)) {
    // Facilitators run their forum entirely from the unified My Forum page
    // (its Health & Goals tab holds the old per-forum dashboard), so they get
    // no Dashboard entry here.
    leadershipItems.push({ path: '/admin/members', label: 'Members', icon: Users });
    leadershipItems.push({ path: '/admin/teachings', label: 'Knowledge Base', icon: BookOpen });
    leadershipItems.push({ path: '/reimbursements', label: 'Reimbursements', icon: Receipt });
  }
  if (isAdminRole(user?.role)) {
    // Org-wide anonymized analytics.
    adminOnlyItems.push({ path: '/organization', label: 'Dashboard', icon: BarChart3 });
    // Org-wide / multi-forum push. Facilitators reach their own forum from
    // the My Forum page's Message Forum modal instead.
    adminOnlyItems.push({ path: '/broadcast', label: 'Broadcast', icon: Megaphone });
    adminOnlyItems.push({ path: '/admin/emails', label: 'Emails', icon: Mail });
    // Forum creation, pricing, and facilitator assignment live on the unified
    // My Forum page now — no separate Forums console.
    adminOnlyItems.push({ path: '/admin/requirements', label: 'Data Requests', icon: ClipboardList });
    adminOnlyItems.push({ path: '/admin/sponsors', label: 'Sponsors', icon: Heart });
  }
  if (isGodModeRole(user?.role)) {
    // Platform-owner-only surfaces: prompt management and org-wide feedback
    // triage are deliberately withheld from regular admins/facilitators.
    adminOnlyItems.push({ path: '/admin/prompts', label: 'Prompts', icon: FileText });
  }
  if (user?.can_manage_tickets || isGodModeRole(user?.role)) {
    adminOnlyItems.push({ path: '/admin/tickets', label: 'Tickets', icon: Inbox });
  }
  if (user?.is_decision_maker) {
    adminOnlyItems.push({ path: '/decisions', label: 'Decisions', icon: Gavel });
  }
  const adminItems = [...leadershipItems, ...adminOnlyItems];

  const isAdminPath = adminItems.some(item => location.pathname === item.path);

  // Global back button for sub-pages (roster, settings, admin screens, …).
  // Top-level nav destinations don't need one, pages with their own back link
  // (member/snapshot detail, Jarvis) are excluded, and Android already gets
  // the header back arrow above.
  const topLevelPaths = [
    ...primaryItems.map((item) => item.path.split('?')[0]),
    ...connectItems.map((item) => item.path.split('?')[0]),
    ...sharpenItems.map((item) => item.path.split('?')[0]),
    ...growItems.map((item) => item.path.split('?')[0]),
  ];
  const selfManagedBackPrefixes = ['/members/', '/snapshots/', '/jarvis'];
  const onJarvis =
    location.pathname === '/jarvis' || location.pathname.startsWith('/jarvis/');
  const onMessages = location.pathname === '/messages';
  const onSnapshotWizard = location.pathname === '/snapshots/new';
  const askIfReturnId = onJarvis ? null : peekAskIfReturnConversation();
  const showBackButton =
    Boolean(askIfReturnId) ||
    (platform !== 'android' &&
      !topLevelPaths.includes(location.pathname) &&
      !selfManagedBackPrefixes.some((p) => location.pathname.startsWith(p)));

  const handleBack = () => {
    if (askIfReturnId) {
      clearAskIfReturnConversation();
      navigate(askIfReturnPath(askIfReturnId));
      return;
    }
    // Deep links (or a fresh tab) have no in-app history to go back to.
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div
      data-app-shell
      className="flex h-full flex-col overflow-hidden bg-off-white lg:block lg:h-auto lg:min-h-screen lg:overflow-visible"
    >
      {/* Header */}
      <header data-app-header className="relative z-40 shrink-0 bg-navy text-white shadow-lg lg:static">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-12 justify-between items-center gap-2 lg:h-16">
            {/* Logo + Android Back */}
            <div className="flex items-center shrink-0">
              {platform === 'android' && location.pathname !== '/' && (
                <button
                  onClick={handleBack}
                  className="p-2 -ml-2 mr-1 rounded-lg hover:bg-white/10 transition-colors"
                  aria-label={askIfReturnId ? 'Back to Ask IF' : 'Go back'}
                >
                  <ArrowLeft size={22} />
                </button>
              )}
              <Link to="/" className="flex items-center" aria-label="Iron Forums home">
                <BrandLogo variant="white" hideTagline className="h-9 w-9 lg:h-11 lg:w-11" />
              </Link>
            </div>

            {/* Desktop Navigation. Labels start at 1080px so iPad landscape
                (1133–1210px, no hover tooltips) gets them; narrower, an admin's
                labelled row no longer fits. */}
            <nav className="hidden lg:flex items-center space-x-0.5">
              {primaryItems.map((item) => {
                const Icon = item.icon;
                const isActive = isItemActive(item.path);
                const locked = isGuest && guestLockedPaths.has(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    title={item.label}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={locked ? handleLockedNav : undefined}
                    className={`flex items-center gap-1.5 px-2 pointer-coarse:px-3 xl:px-3 h-10 rounded-lg transition-colors relative ${
                      isActive
                        ? 'bg-gold text-white'
                        : 'text-white/80 hover:bg-white/10'
                    }`}
                  >
                    <Icon size={16} />
                    <span className="font-medium text-sm hidden min-[1080px]:inline whitespace-nowrap">{item.label}</span>
                    {item.badge && (
                      <span aria-hidden="true" className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center font-bold text-[10px]">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}

              {navSections.map((section) => {
                const SectionIcon = section.icon;
                const sectionOpen = openMenu === section.id;
                const sectionActive = isSectionActive(section.items);
                return (
                  <div key={section.id} className="relative" data-nav-dropdown>
                    <button
                      onClick={() => setOpenMenu(sectionOpen ? null : section.id)}
                      aria-expanded={sectionOpen}
                      className={`flex items-center gap-1.5 px-2 pointer-coarse:px-3 xl:px-3 h-10 rounded-lg transition-colors relative ${
                        sectionActive
                          ? 'bg-gold text-white'
                          : 'text-white/80 hover:bg-white/10'
                      }`}
                    >
                      <SectionIcon size={16} />
                      <span className="font-medium text-sm hidden min-[1080px]:inline whitespace-nowrap">{section.label}</span>
                      <ChevronDown size={14} className={`transition-transform ${sectionOpen ? 'rotate-180' : ''}`} />
                      {section.badge ? (
                        <span aria-hidden="true" className="absolute -top-1 -right-1 bg-red-500 text-white w-4 h-4 rounded-full flex items-center justify-center font-bold text-[10px]">
                          {section.badge}
                        </span>
                      ) : null}
                    </button>

                    {sectionOpen && (
                      <div className="absolute left-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                        {section.items.map((sectionItem) => {
                          const ItemIcon = sectionItem.icon;
                          const itemActive = isItemActive(sectionItem.path);
                          const locked = isGuest && guestLockedPaths.has(sectionItem.path);
                          return (
                            <Link
                              key={sectionItem.path}
                              to={sectionItem.path}
                              onClick={locked ? handleLockedNav : () => setOpenMenu(null)}
                              aria-current={itemActive ? 'page' : undefined}
                              className={`flex items-center gap-2 px-4 py-2 pointer-coarse:py-2.5 text-sm transition-colors ${
                                itemActive
                                  ? 'bg-gold/10 text-gold'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              <ItemIcon size={16} />
                              <span>{sectionItem.label}</span>
                              {sectionItem.badge ? (
                                <span aria-hidden="true" className="ml-auto bg-red-500 text-white text-[11px] font-bold px-1.5 py-0.5 rounded-full">
                                  {sectionItem.badge}
                                </span>
                              ) : null}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              <Link
                to="/jarvis"
                aria-current={location.pathname === '/jarvis' ? 'page' : undefined}
                className={`flex items-center gap-1.5 px-2 pointer-coarse:px-3 xl:px-3 h-10 rounded-lg transition-colors font-medium text-sm ${
                  location.pathname === '/jarvis'
                    ? 'bg-gold text-white'
                    : 'bg-gold/20 text-white hover:bg-gold/30'
                }`}
                title="Ask IF"
              >
                <AskIfMark size={22} showLabel={false} />
                <span className="hidden min-[1080px]:inline whitespace-nowrap">Ask IF</span>
              </Link>
              
              {/* Admin Dropdown */}
              {adminItems.length > 0 && (
                <div className="relative" data-nav-dropdown>
                  <button
                    onClick={() => setOpenMenu(openMenu === 'admin' ? null : 'admin')}
                    aria-expanded={openMenu === 'admin'}
                    className={`flex items-center gap-1.5 px-2 pointer-coarse:px-3 xl:px-3 h-10 rounded-lg transition-colors ${
                      isAdminPath
                        ? 'bg-gold text-white'
                        : 'text-white/80 hover:bg-white/10'
                    }`}
                  >
                    <Shield size={16} />
                    <span className="font-medium text-sm hidden min-[1080px]:inline whitespace-nowrap">Admin</span>
                    <ChevronDown size={14} className={`transition-transform ${openMenu === 'admin' ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {openMenu === 'admin' && (
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      {[
                        { items: leadershipItems, key: 'leadership' },
                        { items: adminOnlyItems, key: 'admin-only' },
                      ]
                        .filter((group) => group.items.length > 0)
                        .map((group, groupIdx) => (
                          <div
                            key={group.key}
                            className={groupIdx > 0 ? 'border-t border-gray-200 mt-1 pt-1' : undefined}
                          >
                            {group.items.map((item) => {
                              const Icon = item.icon;
                              const isActive = location.pathname === item.path;
                              return (
                                <Link
                                  key={item.path}
                                  to={item.path}
                                  onClick={() => setOpenMenu(null)}
                                  aria-current={isActive ? 'page' : undefined}
                                  className={`flex items-center gap-2 px-4 py-2 pointer-coarse:py-2.5 text-sm transition-colors ${
                                    isActive
                                      ? 'bg-gold/10 text-gold'
                                      : 'text-gray-700 hover:bg-gray-100'
                                  }`}
                                >
                                  <Icon size={16} />
                                  <span>{item.label}</span>
                                </Link>
                              );
                            })}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </nav>

            {/* User Menu */}
            <div className="hidden lg:flex items-center space-x-1 shrink-0">
              {/* Name/role only appear at 2xl — at xl the nav labels are shown
                  and an admin's fuller menu already fills the row. */}
              <div className="text-right hidden 2xl:block mr-1 max-w-44">
                <p className="text-sm font-semibold truncate">{user?.full_name}</p>
                <p className="text-xs text-white/70 truncate">{roleLabel(user?.role)}</p>
              </div>
              <button
                onClick={() => setSupportOpen(true)}
                className="relative p-2 pointer-coarse:p-[11px] rounded-lg hover:bg-white/10 transition-colors"
                title={supportBadge ? 'Support & Feedback — the team replied' : 'Support & Feedback'}
              >
                <HelpCircle size={18} />
                {supportBadge > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-500 text-[10px] font-bold leading-[1.1rem] text-white text-center">
                    {supportBadge}
                  </span>
                )}
              </button>
              <Link
                to="/settings"
                className="p-2 pointer-coarse:p-[11px] rounded-lg hover:bg-white/10 transition-colors"
                title="Settings"
              >
                <Settings size={18} />
              </Link>
              <button
                onClick={handleLogout}
                className="p-2 pointer-coarse:p-[11px] rounded-lg hover:bg-white/10 transition-colors"
                title="Logout"
              >
                <LogOut size={18} />
              </button>
            </div>

            {/* Mobile Menu Button — dot signals unseen badges inside the menu */}
            <button
              className="lg:hidden p-2 relative"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              {anyBadge && !mobileMenuOpen && (
                <span aria-hidden="true" className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-navy" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation — full-height overlay so the page behind cannot scroll */}
        {mobileMenuOpen && (
          <div className="fixed inset-x-0 bottom-0 top-[calc(3rem+env(safe-area-inset-top))] z-50 overflow-y-auto overscroll-contain bg-navy lg:hidden">
            <nav className="px-4 py-3 space-y-1 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
              {primaryItems.map((item) => {
                const Icon = item.icon;
                const isActive = isItemActive(item.path);
                const locked = isGuest && guestLockedPaths.has(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={locked ? handleLockedNav : () => setMobileMenuOpen(false)}
                    aria-current={isActive ? 'page' : undefined}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg relative ${
                      isActive
                        ? 'bg-gold text-white'
                        : 'text-white/80 hover:bg-white/10'
                    }`}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                    {item.badge && (
                      <span aria-hidden="true" className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full ml-auto font-bold">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}

              {navSections.map((section) => (
                <React.Fragment key={section.id}>
                  <div className="pt-1">
                    <span className="px-4 text-xs text-white/50 uppercase tracking-wide">{section.label}</span>
                  </div>
                  {section.items.map((sectionItem) => {
                    const ItemIcon = sectionItem.icon;
                    const itemActive = isItemActive(sectionItem.path);
                    const locked = isGuest && guestLockedPaths.has(sectionItem.path);
                    return (
                      <Link
                        key={sectionItem.path}
                        to={sectionItem.path}
                        onClick={locked ? handleLockedNav : () => setMobileMenuOpen(false)}
                        aria-current={itemActive ? 'page' : undefined}
                        className={`flex items-center space-x-3 px-4 py-3 rounded-lg ${
                          itemActive
                            ? 'bg-gold text-white'
                            : 'text-white/80 hover:bg-white/10'
                        }`}
                      >
                        <ItemIcon size={20} />
                        <span>{sectionItem.label}</span>
                        {sectionItem.badge ? (
                          <span aria-hidden="true" className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full ml-auto font-bold">
                            {sectionItem.badge}
                          </span>
                        ) : null}
                      </Link>
                    );
                  })}
                </React.Fragment>
              ))}
              
              {/* Ask Jarvis (lives in the desktop utility cluster) */}
              <div className="border-t border-white/20 my-2 pt-1"></div>
              <Link
                to="/jarvis"
                onClick={() => setMobileMenuOpen(false)}
                aria-current={location.pathname === '/jarvis' ? 'page' : undefined}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg ${
                  location.pathname === '/jarvis'
                    ? 'bg-gold text-white'
                    : 'text-white/80 hover:bg-white/10'
                }`}
              >
                <AskIfMark size={22} showLabel={false} />
                <span>Ask IF</span>
              </Link>

              {/* Admin items in mobile */}
              {adminItems.length > 0 && (
                <>
                  <div className="border-t border-white/20 my-2 pt-2">
                    <span className="px-4 text-xs text-white/50 uppercase tracking-wide">Admin</span>
                  </div>
                  {adminItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        aria-current={isActive ? 'page' : undefined}
                        className={`flex items-center space-x-3 px-4 py-3 rounded-lg ${
                          isActive
                            ? 'bg-gold text-white'
                            : 'text-white/80 hover:bg-white/10'
                        }`}
                      >
                        <Icon size={20} />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </>
              )}
              
              <div className="border-t border-white/20 my-2 pt-2"></div>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  setSupportOpen(true);
                }}
                className="flex items-center space-x-3 px-4 py-3 w-full text-left text-white/80 hover:bg-white/10 rounded-lg"
              >
                <HelpCircle size={20} />
                <span>Support & Feedback</span>
                {supportBadge > 0 && (
                  <span className="ml-auto min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 text-xs font-bold leading-5 text-white text-center">
                    {supportBadge}
                  </span>
                )}
              </button>
              <Link
                to="/settings"
                onClick={() => setMobileMenuOpen(false)}
                aria-current={location.pathname === '/settings' ? 'page' : undefined}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg ${
                  location.pathname === '/settings'
                    ? 'bg-gold text-white'
                    : 'text-white/80 hover:bg-white/10'
                }`}
              >
                <Settings size={20} />
                <span>Settings</span>
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-3 px-4 py-3 w-full text-left text-white/80 hover:bg-white/10 rounded-lg"
              >
                <LogOut size={20} />
                <span>Logout</span>
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main
        data-app-main
        className={`min-h-0 flex-1 lg:max-w-7xl lg:mx-auto ${
          fullBleedMobile
            ? 'flex flex-col overflow-hidden px-4 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:block lg:overflow-visible lg:px-8 lg:py-8 lg:pb-8'
            : 'overflow-y-auto overflow-x-hidden overscroll-y-contain px-4 py-6 pb-[max(6rem,calc(5rem+env(safe-area-inset-bottom)))] sm:px-6 lg:overflow-visible lg:px-8 lg:py-8 lg:pb-8'
        }`}
      >
        {showBackButton && (
          <button
            onClick={handleBack}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-navy transition mb-4 -mt-2 pointer-coarse:py-2.5 pointer-coarse:-my-2.5 pointer-coarse:mb-1.5"
          >
            <ArrowLeft size={16} />
            {askIfReturnId ? 'Back to Ask IF' : 'Back'}
          </button>
        )}
        <LapsedMembershipBanner />
        {children}
      </main>

      {!onJarvis && !onMessages && !onSnapshotWizard && !mobileMenuOpen && <AskIfFab />}

      <SupportFeedbackModal
        isOpen={supportOpen}
        onClose={() => setSupportOpen(false)}
        unreadReplies={supportBadge}
      />
      <MembersOnlyModal
        isOpen={membersOnlyOpen}
        onClose={() => setMembersOnlyOpen(false)}
        reason={user?.limit_app_access ? 'paused' : 'guest'}
      />
    </div>
  );
}
